import { useApolloClient, useQuery, useSubscription } from '@apollo/client'
import { Badge, IconButton, Tooltip } from '@material-ui/core'
import NotificationsIcon from '@material-ui/icons/Notifications'
import { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  NOTIFICATION_INBOX_QUERY,
  NOTIFICATION_RECEIVED_SUBSCRIPTION,
  NOTIFICATION_UNREAD_COUNT_QUERY
} from '../../graphql/notifications'
import { getTranslate, lang } from '../../localization'

export const isNewNotificationHint = (
  previousId: string | undefined,
  nextId: string | undefined
) => Boolean(nextId && previousId !== nextId)

export default function NotificationBell() {
  const history = useHistory()
  const client = useApolloClient()
  const { data, refetch: refetchUnread } = useQuery(
    NOTIFICATION_UNREAD_COUNT_QUERY,
    {
      fetchPolicy: 'cache-and-network'
    }
  )
  const subscription = useSubscription(NOTIFICATION_RECEIVED_SUBSCRIPTION)
  const lastHint = useRef<string>()
  const refreshTimer = useRef<number>()
  const unread = data?.notificationUnreadCount ?? 0

  useEffect(() => {
    const notificationId =
      subscription.data?.notificationReceived?.notificationId
    if (!isNewNotificationHint(lastHint.current, notificationId)) return
    lastHint.current = notificationId
    window.clearTimeout(refreshTimer.current)
    refreshTimer.current = window.setTimeout(() => {
      toast.info(getTranslate('New notification received.'), {
        autoClose: 2500
      })
      Promise.all([
        refetchUnread(),
        client.refetchQueries({
          include: 'active',
          onQueryUpdated: (observable) =>
            observable.query === NOTIFICATION_INBOX_QUERY
              ? observable.refetch()
              : false
        })
      ]).catch(() =>
        toast.error(getTranslate('Unable to refresh notifications.'))
      )
    }, 250)
    return () => window.clearTimeout(refreshTimer.current)
  }, [client, refetchUnread, subscription.data])

  useEffect(() => {
    if (subscription.error) {
      toast.error(
        getTranslate(
          'Live notifications unavailable; inbox remains available.'
        ),
        { autoClose: 4000 }
      )
    }
  }, [subscription.error])

  return (
    <Tooltip title={getTranslate('Notifications')}>
      <IconButton
        color="inherit"
        aria-label={getTranslate('Notifications, {count} unread', {
          count: unread
        })}
        onClick={() => history.push(`/${lang}/notifications`)}>
        <Badge badgeContent={unread} color="secondary" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  )
}
